import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";
import PropTypes from "prop-types";
import {
  createMappingProfile,
  fetchConnectors,
  fetchEntityFields,
  fetchMappingProfile,
  updateMappingProfile,
} from "@accredo/fe-shared/services/importMappingProfileService.js";
import MatchStrategySelector from "./components/MatchStrategySelector.jsx";
import MappingRuleEditor from "./components/MappingRuleEditor.jsx";
import SourceOptionsEditor from "./components/SourceOptionsEditor.jsx";

const record_type_OPTIONS = [
  { value: "gesellschaft", label: "Gesellschaft" },
  { value: "kunde", label: "Kunde" },
  { value: "vertrag", label: "Vertrag" },
  { value: "objekt", label: "Objekt" },
  { value: "objektsparte", label: "Objektsparte" },
  { value: "sparte", label: "Sparte" },
  { value: "user", label: "Benutzer" },
  { value: "bank_account", label: "Bankkonto" },
  { value: "user_contract_assignments", label: "Benutzerzuweisungen" },
];

const DEFAULT_FORM = {
  import_connector_id: "",
  record_type: "",
  match_entity_column: "",
  external_ref_column: null,
  write_mode: "update_or_create",
  external_ref_mode: "entity_table",
  mappings: [],
  source_options: null,
  is_active: true,
};

function ImportMappingProfileFormModal({ open, onClose, profileId }) {
  const isEdit = Boolean(profileId);
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [connectors, setConnectors] = useState([]);
  const [entity_fields, setEntityFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchConnectors()
      .then((res) => setConnectors(res.data ?? []))
      .catch(() =>
        enqueueSnackbar("Connectoren konnten nicht geladen werden", {
          variant: "error",
        })
      );
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return;
    if (!isEdit) {
      setForm(DEFAULT_FORM);
      return;
    }

    setLoading(true);
    fetchMappingProfile(profileId)
      .then((res) => {
        const p = res.data;
        setForm({
          import_connector_id: p.import_connector_id,
          record_type: p.record_type,
          match_entity_column: p.match_entity_column ?? "",
          external_ref_column: p.external_ref_column ?? null,
          write_mode: p.write_mode,
          external_ref_mode: p.external_ref_mode ?? "entity_table",
          mappings: p.mappings ?? [],
          source_options: p.source_options ?? null,
          is_active: p.is_active,
        });
      })
      .catch(() =>
        enqueueSnackbar("Profil konnte nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  }, [open, profileId, isEdit, enqueueSnackbar]);

  useEffect(() => {
    if (!form.record_type) {
      setEntityFields({});
      return;
    }
    fetchEntityFields(form.record_type)
      .then((res) => setEntityFields(res.data ?? {}))
      .catch(() => setEntityFields({}));
  }, [form.record_type]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  async function handleSave() {
    const invalid_index = form.mappings.findIndex(
      (r) => r.transform?.type !== "concat" && !r.source_column?.trim()
    );
    if (invalid_index !== -1) {
      enqueueSnackbar(
        `Regel ${invalid_index + 1}: Quellspalte ist erforderlich`,
        { variant: "warning" }
      );
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await updateMappingProfile(profileId, {
          match_entity_column: form.match_entity_column,
          external_ref_column: form.external_ref_column,
          write_mode: form.write_mode,
          external_ref_mode: form.external_ref_mode,
          mappings: form.mappings,
          source_options: form.source_options,
          is_active: form.is_active,
        });
      } else {
        await createMappingProfile({
          import_connector_id: form.import_connector_id,
          record_type: form.record_type,
          match_entity_column: form.match_entity_column,
          external_ref_column: form.external_ref_column,
          write_mode: form.write_mode,
          external_ref_mode: form.external_ref_mode,
          mappings: form.mappings,
          source_options: form.source_options,
          is_active: form.is_active,
        });
      }
      enqueueSnackbar("Profil gespeichert", { variant: "success" });
      onClose(true);
    } catch (err) {
      const msg = err?.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(", ")
        : "Speichern fehlgeschlagen";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="lg">
      <DialogTitle>
        {isEdit ? "Mapping-Profil bearbeiten" : "Neues Mapping-Profil"}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={3} pt={1}>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <FormControl
                size="small"
                sx={{ minWidth: 220 }}
                disabled={isEdit}
              >
                <InputLabel>Connector</InputLabel>
                <Select
                  value={form.import_connector_id}
                  label="Connector"
                  onChange={(e) => set("import_connector_id", e.target.value)}
                >
                  {connectors.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name ?? c.slug}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                size="small"
                sx={{ minWidth: 220 }}
                disabled={isEdit}
              >
                <InputLabel>Entitätstyp</InputLabel>
                <Select
                  value={form.record_type}
                  label="Entitätstyp"
                  onChange={(e) => set("record_type", e.target.value)}
                >
                  {record_type_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => set("is_active", e.target.checked)}
                  />
                }
                label="Aktiv"
              />
            </Box>

            <Divider />

            <MappingRuleEditor
              mappings={form.mappings}
              entity_fields={entity_fields}
              onChange={(m) => set("mappings", m)}
            />

            <Divider />

            <MatchStrategySelector
              mappings={form.mappings}
              match_entity_column={form.match_entity_column}
              external_ref_column={form.external_ref_column}
              write_mode={form.write_mode}
              external_ref_mode={form.external_ref_mode}
              onChange={(key, value) => set(key, value)}
            />

            <Divider />

            <SourceOptionsEditor
              source_options={form.source_options}
              onChange={(o) => set("source_options", o)}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box flexGrow={1} />
        <DsButton
          variant="tertiary"
          onClick={() => onClose(false)}
          disabled={saving}
        >
          Abbrechen
        </DsButton>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ImportMappingProfileFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  profileId: PropTypes.number,
};

export default ImportMappingProfileFormModal;
